import { useState, useMemo } from 'react';
import Sentry from 'application/exception_reporter';
import { getCountryCode } from 'utilities/locales';
import AddressValidationClient from 'clients/address_validation';
import { JP_PREFECTURE_MAPPINGS } from 'constants/mappings';
import { SHIPPING_INFORMATION_VALIDATION } from 'constants/cdp_events';

// These statuses dictate how we should handle the address validation result on the frontend
export const VALIDATION_STATUSES = {
  VERIFIED: 'verified',
  PARTIALLY_VERIFIED: 'partiallyVerified',
  FAILED_VERIFICATION: 'failed',
};

// these are the possible validation results from our backend
// app/controllers/sq/concerns/address_validation_response_converter_concern.rb
const VALIDATION_RESULTS = {
  VALIDATED: 'validated',
  VALIDATED_BY_PRIMARY: 'validated_by_primary',
  FAILED: 'failed',
  VALIDATION_SERVICE_ERROR: 'validation_service_error',
  UNKNOWN_STATUS: 'unknown_status',
};

// statuses where we will just let the user continue with their address as is
const FAILED_VALIDATION_CALL_RESULTS = new Set([
  VALIDATION_RESULTS.VALIDATION_SERVICE_ERROR,
  VALIDATION_RESULTS.UNKNOWN_STATUS,
]);

// these are the possible CDP names for the address validation statuses
export const VALIDATION_STATUS_TO_CDP_NAME = {
  [VALIDATION_STATUSES.VERIFIED]: SHIPPING_INFORMATION_VALIDATION.VERIFIED,
  [VALIDATION_STATUSES.PARTIALLY_VERIFIED]:
    SHIPPING_INFORMATION_VALIDATION.PARTIALLY_VERIFIED,
  [VALIDATION_STATUSES.FAILED_VERIFICATION]:
    SHIPPING_INFORMATION_VALIDATION.FAILED_VERIFICATION,
};

// https://prototype.sqprod.co/#/docs/squareup.address.service.AddressResult.Status
// potentially may need to support more of these statuses in the future
const ADDRESS_RESULT_STATUS = {
  SUCCESS: 'SUCCESS',
  FAILED_VERIFICATION: 'FAILED_VERIFICATION',
};

// https://prototype.sqprod.co/#/docs/squareup.address.service.AddressResult.Status
// for now we only care about this status
const VERIFICATION_STATUS = {
  PARTIALLY_VERIFIED: 'PARTIALLY_VERIFIED',
};

/**
 * @param {Object} addressValidationResponse - the response from the address validation call
 * @returns {string} - the status of the address validation
 */
export const computeAddressValidationResultStatus = (
  addressValidationResponse,
) => {
  if (!addressValidationResponse) {
    throw new Error(
      'Computing address validation result status: No address validation response',
    );
  }

  const {
    result: addressValidationResult,
    address_service_statuses: addressServiceStatuses,
  } = addressValidationResponse;

  if (!addressServiceStatuses) {
    throw new Error(
      'Computing address validation result status: No address service statuses',
    );
  }

  const {
    status: addressResultStatus,
    verification_status: verificationStatus,
  } = addressServiceStatuses;

  // if the address validation call itself failed, we let the user continue with their address as is
  if (FAILED_VALIDATION_CALL_RESULTS.has(addressValidationResult)) {
    throw new Error(
      'Computing address validation result status: Failed validation call',
    );
  }

  // https://prototype.sqprod.co/#/docs/squareup.address.service.AddressResult.Status
  switch (addressResultStatus) {
    case ADDRESS_RESULT_STATUS.SUCCESS: {
      // if address is verified, we send the address foward with corrections auto applied for the user
      return VALIDATION_STATUSES.VERIFIED;
    }
    case ADDRESS_RESULT_STATUS.FAILED_VERIFICATION: {
      // if the address is partially verified, we show the user the corrected address and ask them to correct or confirm
      if (verificationStatus === VERIFICATION_STATUS.PARTIALLY_VERIFIED) {
        return VALIDATION_STATUSES.PARTIALLY_VERIFIED;
      }

      // else, we tell them we can't verify the address, and ask them to make corrections or confirm and continue with their address
      return VALIDATION_STATUSES.FAILED_VERIFICATION;
    }
    default: {
      // if the address has any other status, we let them pass with their address as is
      throw new Error(
        'Computing address validation result status: Unknown address result status',
      );
    }
  }
};

/**
 * @param {string} prefectureKanji - the Kanji characters for the prefecture (administrative_district_level_1)
 * @returns {string} - the ISO code for the prefecture
 */
export const kanjiToISO = (prefectureKanji) => {
  return JP_PREFECTURE_MAPPINGS[prefectureKanji];
};

/**
 * @param {string} prefectureISO - the ISO code for the prefecture
 * @returns {string} - the Kanji characters for the prefecture
 */
export const ISOToKanji = (prefectureISO) => {
  return Object.keys(JP_PREFECTURE_MAPPINGS).find(
    (key) => JP_PREFECTURE_MAPPINGS[key] === prefectureISO,
  );
};

export const useAddressValidation = () => {
  const [isAddressValidating, setIsAddressValidating] = useState(false);
  const addressValidationClient = useMemo(
    () => new AddressValidationClient(),
    [],
  );

  const validateAddress = async (address) => {
    setIsAddressValidating(true);
    const countryCode = getCountryCode().toUpperCase();
    const addressValidationHash = {
      ...address,
      // we need to convert the prefecture from the ISO code to the Kanji characters
      // in order for the address service to correctly validate the address
      administrative_district_level_1:
        countryCode === 'JP'
          ? ISOToKanji(address.administrative_district_level_1)
          : address.administrative_district_level_1,
      country_code: countryCode,
    };

    try {
      const { address_validation_result: addressValidationResult } =
        await addressValidationClient.validateAddress(addressValidationHash);

      const validationStatus = computeAddressValidationResultStatus(
        addressValidationResult,
      );

      const correctedAddress = addressValidationResult.corrected_address;

      return {
        addressValidationResponse: addressValidationResult,
        validationStatus,
        hasChangedFields: addressValidationResult.changed_fields.length > 0,
        addressIsVerified: validationStatus === VALIDATION_STATUSES.VERIFIED,
        correctedAddress: {
          ...correctedAddress,
          administrative_district_level_1:
            countryCode === 'JP'
              ? kanjiToISO(correctedAddress.administrative_district_level_1)
              : correctedAddress.administrative_district_level_1,
        },
        trackingParams: {
          shop_validation_status: validationStatus,
          service_result_status:
            addressValidationResult?.address_service_statuses?.status,
          service_verification_status:
            addressValidationResult?.address_service_statuses
              ?.verification_status,
        },
      };
    } catch (error) {
      Sentry.capture(error);
      throw new Error('Failed to validate address', error);
    } finally {
      setIsAddressValidating(false);
    }
  };

  return {
    isAddressValidating,
    validateAddress,
  };
};
